import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '../context/CartContext';
import BucksSearch from './BucksSearch';
import '../stylings/BucksHeader.css';

function BucksHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [overlayRoot, setOverlayRoot] = useState(null);
  
  // Get cart data from context instead of local state
  const { cartItems, removeFromCart, itemCount, subtotal } = useCart();
  
  // Get current path for active link handling
  const currentPath = window.location.pathname;

  // Create overlay root for iOS Safari
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      let overlayContainer = document.getElementById('ios-overlay-root');
      if (!overlayContainer) {
        overlayContainer = document.createElement('div');
        overlayContainer.id = 'ios-overlay-root';
        overlayContainer.className = 'ios-overlay-root';
        document.body.appendChild(overlayContainer);
      }
      setOverlayRoot(overlayContainer);
      
      return () => {
        if (overlayContainer && overlayContainer.parentNode) {
          overlayContainer.parentNode.removeChild(overlayContainer);
        }
      };
    }
  }, []);

  useEffect(() => {
    // Add scroll event listener
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Add scroll detection for better visual feedback
  useEffect(() => {
    const cartItemsElement = document.querySelector('.cart-items');
    const cartDropdown = document.querySelector('.cart-dropdown');
    
    if (cartItemsElement && cartDropdown) {
      const checkScroll = () => {
        if (cartItemsElement.scrollHeight > cartItemsElement.clientHeight) {
          cartDropdown.classList.add('has-scroll');
        } else {
          cartDropdown.classList.remove('has-scroll');
        }
      };
      
      // Check on cart content change
      const observer = new MutationObserver(checkScroll);
      observer.observe(cartItemsElement, { childList: true, subtree: true });
      
      // Initial check
      checkScroll();
      
      return () => observer.disconnect();
    }
  }, [cartOpen, cartItems]);

  // This hook centrally manages body scrolling when any overlay is open.
  useEffect(() => {
    // If any overlay is open, disable body scroll to improve user experience
    if (menuOpen || cartOpen || searchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  
    // Cleanup function to restore scroll when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, cartOpen, searchOpen]); // Reruns whenever an overlay's state changes

  // Add click outside functionality for mobile cart
  useEffect(() => {
    // Close cart when clicking outside on mobile
    const handleClickOutside = (event) => {
      if (cartOpen) {
        const cartContent = document.querySelector('.cart-content');
        const cartButton = document.querySelector('.cart-button');
        
        if (cartContent && !cartContent.contains(event.target) && !cartButton.contains(event.target)) {
          // Check if click is on the backdrop
          if (event.target.classList.contains('cart-dropdown')) {
            setCartOpen(false);
          }
        }
      }
    };

    // Handle escape key to close cart
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && cartOpen) {
        setCartOpen(false);
      }
    };

    if (cartOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [cartOpen]);

  // Enhanced iOS Safari overlay management - FIXED to prevent layout issues
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      const overlayContainer = document.getElementById('ios-overlay-root');
      
      if (overlayContainer) {
        if (cartOpen || menuOpen || searchOpen) {
          // Only add active class, don't modify transforms on body/app
          overlayContainer.classList.add('active');
          document.body.classList.add('ios-overlay-active');
          
          // REMOVED: Transform and z-index manipulations that cause white space
          // Only set essential overlay properties
          overlayContainer.style.zIndex = '2147483647';
          overlayContainer.style.isolation = 'isolate';
          
          // Prevent background scrolling without transforms
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
          
          // Store scroll position to restore later
          const scrollY = window.scrollY;
          document.body.style.top = `-${scrollY}px`;
          document.body.setAttribute('data-scroll-y', scrollY.toString());
          
        } else {
          overlayContainer.classList.remove('active');
          document.body.classList.remove('ios-overlay-active');
          
          // Restore normal scrolling
          const scrollY = parseInt(document.body.getAttribute('data-scroll-y') || '0');
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
          document.body.style.top = '';
          document.body.removeAttribute('data-scroll-y');
          
          // Restore scroll position
          window.scrollTo(0, scrollY);
          
          // Add class to App instead of transform manipulation
          const app = document.querySelector('.App');
          if (app) {
            app.classList.remove('ios-overlay-active');
          }
        }
      }
    }
  }, [cartOpen, menuOpen, searchOpen]);

  // Check if a link should be shown based on current path
  const shouldShowLink = (path) => {
    // Don't show Home link on homepage
    if (path === '/' && (currentPath === '/' || currentPath === '')) return false;
    // Don't show Shop link on shop page
    if (path === '/shop' && currentPath === '/shop') return false;
    // Don't show Buck's Box link on Buck's Box page
    if (path === '/bucks-box' && currentPath === '/bucks-box') return false;
    // Don't show Our Story link on about page
    if (path === '/about' && currentPath === '/about') return false;
    // Don't show Recipes link on recipes page
    if (path === '/recipes' && currentPath === '/recipes') return false;
    // Don't show Contact link on contact page
    if (path === '/contact' && currentPath === '/contact') return false;
    
    return true;
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (cartOpen) setCartOpen(false);
    if (searchOpen) setSearchOpen(false);
  };

  const toggleCart = () => {
    setCartOpen(!cartOpen);
    if (menuOpen) setMenuOpen(false);
    if (searchOpen) setSearchOpen(false);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (menuOpen) setMenuOpen(false);
    if (cartOpen) setCartOpen(false);
  };

  // Close search overlay
  const closeSearch = () => {
    setSearchOpen(false);
  };

  // Close cart dropdown when navigating away
  const closeCart = () => {
    setCartOpen(false);
  };

  // Enhanced calculate total using effective unit prices for special pricing
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const priceToUse = item.effectiveUnitPrice || item.price;
      const itemTotal = priceToUse * item.quantity;
      console.log(`Item: ${item.name}, Quantity: ${item.quantity}, Price: ${priceToUse}, Total: ${itemTotal}`);
      return total + itemTotal;
    }, 0).toFixed(2);
  };
  
  // Inline SVG icons as a fallback solution
  const searchIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
  
  const basketIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h2l.6 3m0 0h16.8M4.6 6l1.2 6m0 0h12.4m-12.4 0l.8 4h10.8l.8-4m-12.4 0L6 6M18 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM10 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path>
    </svg>
  );

  // Reusable cart content component
  const CartContent = () => (
    <>
      <div className="cart-header">
        <h3>Your Basket</h3>
        <button 
          className="cart-close-btn"
          onClick={() => setCartOpen(false)}
          aria-label="Close cart"
        >
          ×
        </button>
      </div>
      
      {(!cartItems || cartItems.length === 0) ? (
        <p className="empty-cart">Your basket is empty</p>
      ) : (
        <>
          <ul className="cart-items">
            {cartItems.map(item => (
              <li key={item.id} className="cart-item">
                <div className="item-details">
                  <span className="item-name">{item.name}</span>
                  <span className="item-quantity">
                    {item.hasSpecialPricing && item.specialPricingInfo ? (
                      // Enhanced special pricing display for corn
                      <div className="special-pricing-display">
                        {item.specialPricingInfo.dozens > 0 && (
                          <span className="dozen-display">
                            🌽 {item.specialPricingInfo.dozens} dozen
                          </span>
                        )}
                        {item.specialPricingInfo.remainder > 0 && (
                          <span className="remainder-display">
                            {item.specialPricingInfo.dozens > 0 ? ' + ' : ''}
                            {item.specialPricingInfo.remainder} ea
                          </span>
                        )}
                        <div className="pricing-info">
                          <span className="effective-price">
                            @ ${item.effectiveUnitPrice.toFixed(2)} avg
                          </span>
                          {item.originalPrice && item.effectiveUnitPrice < item.originalPrice && (
                            <span className="savings-indicator">
                              (Save ${((item.originalPrice - item.effectiveUnitPrice) * item.quantity).toFixed(2)})
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="item-quantity-details">
                        <span>Qty: {item.quantity}</span>
                        <span>@</span>
                        <span>${(item.effectiveUnitPrice || item.price).toFixed(2)} / {item.unit || 'ea'}</span>
                      </div>
                    )}
                  </span>
                </div>
                <span className="item-price">
                  ${((item.effectiveUnitPrice || item.price) * item.quantity).toFixed(2)}
                </span>
                <button 
                  className="remove-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromCart(item.id);
                  }}
                  aria-label="Remove item"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="cart-total">
            <span>Total</span>
            <span>${subtotal ? subtotal.toFixed(2) : calculateTotal()}</span>
          </div>
          <div className="cart-actions">
            <button 
              className="view-cart-btn" 
              onClick={() => {
                closeCart();
                window.location.href = '/basket';
              }}
            >
              View Basket
            </button>
            <button 
              className="checkout-btn"
              onClick={() => {
                closeCart();
                window.location.href = '/checkout';
              }}
            >
              Checkout
            </button>
          </div>
        </>
      )}
    </>
  );

  // Create cart dropdown content for iOS portal
  const cartDropdownContent = (
    <div 
      className="cart-dropdown"
      style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        zIndex: '2147483647',
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        isolation: 'isolate'
      }}
      onClick={(e) => {
        // Close cart if clicking on backdrop
        if (e.target === e.currentTarget) {
          setCartOpen(false);
        }
      }}
    >
      <div 
        className="cart-content"
        style={{
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }}
      >
        <CartContent />
      </div>
    </div>
  );

  // Create mobile navigation content for iOS portal
  const mobileNavContent = (
    <nav 
      className="main-nav"
      style={{
        position: 'fixed',
        top: '0',
        right: '0',
        width: '280px',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.1)',
        display: 'block',
        padding: '5rem 2rem 2rem',
        zIndex: '2147483646',
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden'
      }}
    >
      <ul className="nav-links">
        {shouldShowLink('/') && <li><a href="/">Home</a></li>}
        {shouldShowLink('/shop') && <li><a href="/shop">Shop</a></li>}
        {shouldShowLink('/bucks-box') && <li><a href="/bucks-box">Buck's Box</a></li>}
        {shouldShowLink('/about') && <li><a href="/about">Our Story</a></li>}
        {shouldShowLink('/recipes') && <li><a href="/recipes">Recipes</a></li>}
        {shouldShowLink('/contact') && <li><a href="/contact">Contact</a></li>}
      </ul>
    </nav>
  );

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <>
      <header className={`bucks-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="logo-container">
            <a href="/" className="logo-link">
              <div className="logo">
                <span className="logo-text">Buck's Farmstand</span>
              </div>
            </a>
          </div>

          {/* Desktop navigation - always render for non-iOS or when not mobile */}
          <nav className={`main-nav ${menuOpen && !isIOS ? 'open' : ''}`}>
            <ul className="nav-links">
              {shouldShowLink('/') && <li><a href="/">Home</a></li>}
              {shouldShowLink('/shop') && <li><a href="/shop">Shop</a></li>}
              {shouldShowLink('/bucks-box') && <li><a href="/bucks-box">Buck's Box</a></li>}
              {shouldShowLink('/about') && <li><a href="/about">Our Story</a></li>}
              {shouldShowLink('/recipes') && <li><a href="/recipes">Recipes</a></li>}
              {shouldShowLink('/contact') && <li><a href="/contact">Contact</a></li>}
            </ul>
          </nav>

          <div className="header-actions">
            <button 
              className="search-button" 
              onClick={toggleSearch} 
              aria-label="Search"
            >
              {searchIcon}
            </button>
            
            <div className="cart-container">
              <button className="cart-button" onClick={toggleCart} aria-label="Shopping Basket">
                {basketIcon}
                {(cartItems && cartItems.length > 0) && (
                  <span className="cart-count">{itemCount || cartItems.length}</span>
                )}
              </button>
              
              {/* Desktop cart dropdown (non-iOS) */}
              {cartOpen && !isIOS && (
                <div 
                  className="cart-dropdown"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setCartOpen(false);
                    }
                  }}
                >
                  <div className="cart-content">
                    <CartContent />
                  </div>
                </div>
              )}
            </div>

            <button className="mobile-menu-toggle" onClick={toggleMenu} aria-label="Toggle Menu">
              <span className={`menu-bar ${menuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>
      </header>
      
      {/* iOS Safari overlays using portal */}
      {isIOS && overlayRoot && (
        <>
          {cartOpen && createPortal(cartDropdownContent, overlayRoot)}
          {menuOpen && createPortal(mobileNavContent, overlayRoot)}
        </>
      )}
      
      {/* Search Component */}
      <BucksSearch isOpen={searchOpen} onClose={closeSearch} />
    </>
  );
}

export default BucksHeader;